// Projection-aware GROUP BY column ranking (Workstream C, pgsql 32.§6).
//
// `GROUP BY f.▮` — the columns the statement already projects through that
// alias (`SELECT f.film_id, f.title …`) are near-certain GROUP BY keys; an
// alphabetical full-column listing buries them. Lifts already-referenced
// columns to tier 1 and drops the rest of the tier-1 column block to 2.
//
// Self-gated to the `group-by` clause (mirrors fkColumnRanking's gating) —
// other dotted positions keep their existing ranking signals.

import type { CompletionCandidate } from '@/core/engines';
import { computeClauseContext } from '@/features/sql-grammar/document/editorContextClause';

export function applyProjectionAwareGroupByRanking(args: {
  readonly out: CompletionCandidate[];
  readonly text: string;
  readonly offset: number;
  readonly statementStart: number;
  readonly sqlEngine: string;
  readonly alias: string;
}): void {
  const clause = computeClauseContext(
    args.text,
    args.statementStart,
    args.offset,
    args.sqlEngine,
  )?.clause;
  if (clause !== 'group-by') return;
  const projected = collectAliasColumnRefs(
    args.text.slice(args.statementStart, args.offset),
    args.alias,
  );
  if (projected.size === 0) return;
  const { out } = args;
  if (
    !out.some(
      (c) => c.kind === 'column' && projected.has((c.filterText ?? c.label).toLowerCase()),
    )
  ) {
    return;
  }
  for (let i = 0; i < out.length; i += 1) {
    const c = out[i];
    if (c.kind !== 'column') continue;
    if (projected.has((c.filterText ?? c.label).toLowerCase())) {
      if (c.tier !== 1) out[i] = { ...c, tier: 1 };
    } else if (c.tier === 1) {
      out[i] = { ...c, tier: 2 };
    }
  }
}

/** Folded column names referenced as `<alias>.<col>` in `slice`. */
function collectAliasColumnRefs(slice: string, alias: string): Set<string> {
  const cols = new Set<string>();
  const re = new RegExp(
    `\\b${alias.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\.([A-Za-z_][\\w$]*)`,
    'gi',
  );
  let m: RegExpExecArray | null;
  while ((m = re.exec(slice)) !== null) {
    cols.add(m[1].toLowerCase());
  }
  return cols;
}
